import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  Search,
  Phone,
  MapPin,
  Calendar,
  Edit2,
  Trash2,
  ExternalLink,
  MessageCircle,
  UserCheck,
  TrendingUp,
  X,
  Save,
  User,
  Coins
} from "lucide-react";
import API_BASE from "../../config/api";
import { ConfirmModal } from "./AdminModals";

const TableCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.25);
  width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    border-radius: 10px;
  }
`;

const Toolbar = styled.div`
  padding: 1.15rem 1.35rem;
  border-bottom: 1px solid #1f293d;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    padding: 0.85rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 240px;
  max-width: 450px;

  svg {
    position: absolute;
    left: 0.85rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
  }

  @media (max-width: 640px) {
    min-width: 100%;
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: #0b0f19;
  border: 1px solid #1f293d;
  border-radius: 8px;
  padding: 0.6rem 0.85rem 0.6rem 2.4rem;
  color: #f3f4f6;
  font-size: 0.9rem;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: #f59e0b;
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
  }

  &::placeholder {
    color: #6b7280;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const StatsPills = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const Pill = styled.div`
  background: #161f30;
  border: 1px solid #1f293d;
  padding: 0.35rem 0.65rem;
  border-radius: 7px;
  font-size: 0.78rem;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  strong {
    color: #f59e0b;
    font-weight: 600;
  }

  @media (max-width: 480px) {
    font-size: 0.72rem;
    padding: 0.3rem 0.55rem;
  }
`;

/* 🖥️ Desktop Table View */
const DesktopTableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.88rem;

  th {
    background: #0e1422;
    padding: 0.9rem 1.25rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.6px;
    border-bottom: 1px solid #1f293d;
    white-space: nowrap;
  }

  td {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #1a2234;
    color: #d1d5db;
    vertical-align: middle;
  }

  tbody tr:hover {
    background-color: #161f30;
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.3));
  border: 1px solid rgba(245, 158, 11, 0.4);
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const UserName = styled(Link)`
  color: #f3f4f6;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  transition: color 0.2s;

  &:hover {
    color: #f59e0b;
  }
`;

const PhoneGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9ca3af;

  a {
    color: #6b7280;
    transition: color 0.2s;
    display: flex;
    align-items: center;

    &:hover {
      color: #10b981;
    }
    &.call:hover {
      color: #38bdf8;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const ActionIconBtn = styled.button`
  background: #1f293d;
  border: 1px solid #2b3648;
  color: #9ca3af;
  border-radius: 6px;
  padding: 0.4rem 0.55rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #374151;
    color: #f3f4f6;
  }

  &.view:hover {
    color: #38bdf8;
    border-color: #0284c7;
  }

  &.edit:hover {
    color: #f59e0b;
    border-color: #d97706;
  }

  &.delete:hover {
    color: #ef4444;
    border-color: #dc2626;
  }
`;

/* 📱 Mobile Cards View (Active for screens <= 768px) */
const MobileCardsContainer = styled.div`
  display: none;
  padding: 0.75rem;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const UserMobileCard = styled.div`
  background: #161f30;
  border: 1px solid #1f293d;
  border-radius: 11px;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  min-width: 0;
  overflow: hidden;
`;

const MobileCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
`;

const MobileCardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-size: 0.8rem;
  color: #9ca3af;
  padding: 0.5rem 0;
  border-top: 1px solid #1f293d;
  border-bottom: 1px solid #1f293d;
  min-width: 0;

  .item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .quick-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.2rem 0.5rem;
    border-radius: 5px;
    font-size: 0.72rem;
    font-weight: 600;
    text-decoration: none;

    &.wa {
      background: rgba(16, 185, 129, 0.15);
      color: #10b981;
      border: 1px solid rgba(16, 185, 129, 0.3);
    }

    &.call {
      background: rgba(56, 189, 248, 0.15);
      color: #38bdf8;
      border: 1px solid rgba(56, 189, 248, 0.3);
    }
  }
`;

const MobileCardActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.45rem;
  width: 100%;

  .view-trades {
    grid-column: 1 / -1; /* full width across the top */
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
    border: 1px solid rgba(245, 158, 11, 0.3);
    padding: 0.55rem;
    border-radius: 6px;
    font-size: 0.82rem;
    font-weight: 600;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
  }

  .edit-btn {
    background: #1f293d;
    color: #e5e7eb;
    border: 1px solid #374151;
    padding: 0.45rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    cursor: pointer;
  }

  .del-btn {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
    padding: 0.45rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    cursor: pointer;
  }
`;

// Responsive Edit Modal
const EditModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 0.85rem;
`;

const EditModalContent = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  width: 100%;
  max-width: 460px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.25rem 1rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.6);
  color: #fff;
`;

const ModalTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.15rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid #1f293d;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #f3f4f6;
    display: flex;
    align-items: center;
    gap: 0.45rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 0.95rem;

  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #9ca3af;
    margin-bottom: 0.35rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  input {
    width: 100%;
    background: #0b0f19;
    border: 1px solid #1f293d;
    border-radius: 8px;
    padding: 0.6rem 0.8rem;
    color: #f3f4f6;
    font-size: 0.9rem;
    outline: none;
    transition: all 0.2s;

    &:focus {
      border-color: #f59e0b;
      box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
    }

    @media (max-width: 640px) {
      font-size: 16px;
    }
  }
`;

const EmptyState = styled.div`
  padding: 2.5rem 1rem;
  text-align: center;
  color: #6b7280;

  p {
    margin: 0.5rem 0 0 0;
    font-size: 0.88rem;
  }
`;

const AdminUsersTable = ({ users, setUsers, trades = [], onNotify }) => {
  const [search, setSearch] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const userTradeStats = React.useMemo(() => {
    const map = {};
    if (Array.isArray(trades)) {
      trades.forEach((t) => {
        const uId = t.userId?._id || t.userId;
        if (uId) {
          if (!map[uId]) map[uId] = { count: 0, volume: 0 };
          map[uId].count += 1;
          map[uId].volume += Number(t.totalPrice) || 0;
        }
      });
    }
    return map;
  }, [trades]);

  const filteredUsers = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.fullName && u.fullName.toLowerCase().includes(q)) ||
      (u.phone && u.phone.toLowerCase().includes(q)) ||
      (u.address && u.address.toLowerCase().includes(q))
    );
  });

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setSavingEdit(true);

    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${editingUser._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify({
          fullName: editingUser.fullName,
          phone: editingUser.phone,
          address: editingUser.address,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setUsers(users.map((u) => (u._id === editingUser._id ? { ...u, ...editingUser } : u)));
        setEditingUser(null);
        onNotify?.({ type: "success", message: "User profile updated successfully!" });
      } else {
        onNotify?.({ type: "error", message: data.message || "Failed to update user." });
      }
    } catch (err) {
      console.error("Edit user error:", err);
      onNotify?.({ type: "error", message: "Server connection failed." });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingUserId) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${deletingUserId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      if (res.ok) {
        setUsers(users.filter((u) => u._id !== deletingUserId));
        onNotify?.({ type: "success", message: "User deleted successfully." });
      } else {
        onNotify?.({ type: "error", message: "Failed to delete user." });
      }
    } catch (err) {
      console.error("Delete user error:", err);
      onNotify?.({ type: "error", message: "Error deleting user." });
    } finally {
      setDeletingUserId(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <TableCard>
      {/* 🔍 Top Toolbar */}
      <Toolbar>
        <SearchContainer>
          <Search size={15} />
          <SearchInput
            type="text"
            placeholder="Search customer, phone, city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchContainer>

        <StatsPills>
          <Pill>
            <UserCheck size={13} color="#f59e0b" />
            Total: <strong>{users.length}</strong>
          </Pill>
          <Pill>
            <TrendingUp size={13} color="#10b981" />
            Showing: <strong>{filteredUsers.length}</strong>
          </Pill>
        </StatsPills>
      </Toolbar>

      {/* 👥 Users Display */}
      {filteredUsers.length === 0 ? (
        <EmptyState>
          <User size={34} />
          <p>No registered users found matching your search.</p>
        </EmptyState>
      ) : (
        <>
          {/* 🖥️ Desktop Table View */}
          <DesktopTableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Phone / Contact</th>
                  <th>Address</th>
                  <th>Joined On</th>
                  <th>Activity</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, idx) => {
                  const stats = userTradeStats[user._id] || { count: 0, volume: 0 };
                  const cleanPhone = user.phone ? user.phone.replace(/[^0-9]/g, "") : "";

                  return (
                    <tr key={user._id}>
                      <td style={{ color: "#6b7280", fontWeight: 500 }}>{idx + 1}</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                          <UserAvatar>{getInitials(user.fullName)}</UserAvatar>
                          <div style={{ minWidth: 0, overflow: "hidden" }}>
                            <UserName to={`/admin/user/${user._id}/trades`}>
                              {user.fullName}
                              <ExternalLink size={12} color="#9ca3af" />
                            </UserName>
                            <span style={{ fontSize: "0.74rem", color: "#6b7280" }}>
                              ID: {user._id ? user._id.slice(-6).toUpperCase() : ""}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <PhoneGroup>
                          <span>{user.phone || "-"}</span>
                          {cleanPhone && (
                            <>
                              <a
                                href={`https://wa.me/91${cleanPhone}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Message on WhatsApp"
                              >
                                <MessageCircle size={15} />
                              </a>
                              <a href={`tel:${cleanPhone}`} title="Call Customer" className="call">
                                <Phone size={14} />
                              </a>
                            </>
                          )}
                        </PhoneGroup>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: "#9ca3af", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <MapPin size={13} color="#6b7280" />
                          <span>{user.address || "N/A"}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: "#9ca3af", fontSize: "0.82rem" }}>
                          <Calendar size={13} color="#6b7280" />
                          <span>
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            background: stats.count > 0 ? "rgba(16, 185, 129, 0.15)" : "rgba(107, 114, 128, 0.15)",
                            color: stats.count > 0 ? "#10b981" : "#9ca3af",
                            padding: "0.2rem 0.55rem",
                            borderRadius: "6px",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                        >
                          {stats.count} Trades
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <ActionButtons style={{ justifyContent: "flex-end" }}>
                          <Link to={`/admin/user/${user._id}/trades`}>
                            <ActionIconBtn className="view" title="View Customer Trades">
                              <ExternalLink size={14} />
                            </ActionIconBtn>
                          </Link>
                          <ActionIconBtn
                            className="edit"
                            title="Edit Customer Details"
                            onClick={() => setEditingUser({ ...user })}
                          >
                            <Edit2 size={14} />
                          </ActionIconBtn>
                          <ActionIconBtn
                            className="delete"
                            title="Delete Customer"
                            onClick={() => setDeletingUserId(user._id)}
                          >
                            <Trash2 size={14} />
                          </ActionIconBtn>
                        </ActionButtons>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </DesktopTableWrapper>

          {/* 📱 Mobile Cards View (Zero Overflow on Phones) */}
          <MobileCardsContainer>
            {filteredUsers.map((user) => {
              const stats = userTradeStats[user._id] || { count: 0, volume: 0 };
              const cleanPhone = user.phone ? user.phone.replace(/[^0-9]/g, "") : "";

              return (
                <UserMobileCard key={user._id}>
                  <MobileCardTop>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", minWidth: 0, flex: 1, overflow: "hidden" }}>
                      <UserAvatar>{getInitials(user.fullName)}</UserAvatar>
                      <div style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                        <UserName to={`/admin/user/${user._id}/trades`}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {user.fullName}
                          </span>
                          <ExternalLink size={11} color="#9ca3af" style={{ flexShrink: 0 }} />
                        </UserName>
                        <span style={{ fontSize: "0.7rem", color: "#6b7280" }}>
                          ID: {user._id ? user._id.slice(-6).toUpperCase() : ""}
                        </span>
                      </div>
                    </div>
                    <span
                      style={{
                        background: stats.count > 0 ? "rgba(16, 185, 129, 0.15)" : "rgba(107, 114, 128, 0.15)",
                        color: stats.count > 0 ? "#10b981" : "#9ca3af",
                        padding: "0.2rem 0.5rem",
                        borderRadius: "5px",
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {stats.count} Trades
                    </span>
                  </MobileCardTop>

                  <MobileCardDetails>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.35rem" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontWeight: 600, color: "#f3f4f6" }}>
                        <Phone size={12} color="#f59e0b" />
                        {user.phone || "-"}
                      </span>
                      {cleanPhone && (
                        <div style={{ display: "flex", gap: "0.35rem" }}>
                          <a
                            href={`https://wa.me/91${cleanPhone}`}
                            target="_blank"
                            rel="noreferrer"
                            className="quick-btn wa"
                          >
                            <MessageCircle size={11} /> WhatsApp
                          </a>
                          <a
                            href={`tel:${cleanPhone}`}
                            className="quick-btn call"
                          >
                            <Phone size={11} /> Call
                          </a>
                        </div>
                      )}
                    </div>

                    {user.address && (
                      <div className="item">
                        <MapPin size={12} color="#6b7280" style={{ flexShrink: 0 }} />
                        <span>{user.address}</span>
                      </div>
                    )}

                    <div className="item">
                      <Calendar size={12} color="#6b7280" style={{ flexShrink: 0 }} />
                      <span>
                        Joined:{" "}
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "-"}
                      </span>
                    </div>
                  </MobileCardDetails>

                  <MobileCardActions>
                    <Link to={`/admin/user/${user._id}/trades`} className="view-trades">
                      <Coins size={13} /> View Trades ({stats.count})
                    </Link>
                    <button onClick={() => setEditingUser({ ...user })} className="edit-btn">
                      <Edit2 size={12} /> Edit Details
                    </button>
                    <button onClick={() => setDeletingUserId(user._id)} className="del-btn">
                      <Trash2 size={12} /> Delete User
                    </button>
                  </MobileCardActions>
                </UserMobileCard>
              );
            })}
          </MobileCardsContainer>
        </>
      )}

      {/* ✏️ Edit User Modal */}
      {editingUser && (
        <EditModalOverlay onClick={() => setEditingUser(null)}>
          <EditModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitleRow>
              <h3>
                <Edit2 size={17} color="#f59e0b" />
                Edit Customer Profile
              </h3>
              <button
                onClick={() => setEditingUser(null)}
                style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer" }}
              >
                <X size={17} />
              </button>
            </ModalTitleRow>

            <form onSubmit={handleEditSubmit}>
              <FormGroup>
                <label>Full Name</label>
                <input
                  type="text"
                  required
                  value={editingUser.fullName || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, fullName: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <label>Phone Number</label>
                <input
                  type="text"
                  required
                  value={editingUser.phone || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <label>Address / City</label>
                <input
                  type="text"
                  required
                  value={editingUser.address || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, address: e.target.value })}
                />
              </FormGroup>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.65rem", marginTop: "1.25rem" }}>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  style={{
                    background: "#1f2937",
                    color: "#e5e7eb",
                    border: "1px solid #374151",
                    borderRadius: "7px",
                    padding: "0.55rem 0.95rem",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  style={{
                    background: "linear-gradient(135deg, #f59e0b, #d97706)",
                    color: "#0b0f19",
                    fontWeight: 600,
                    border: "none",
                    borderRadius: "7px",
                    padding: "0.55rem 1.15rem",
                    cursor: savingEdit ? "not-allowed" : "pointer",
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                >
                  <Save size={14} />
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </EditModalContent>
        </EditModalOverlay>
      )}

      {/* 🗑️ Delete User Confirmation */}
      <ConfirmModal
        isOpen={Boolean(deletingUserId)}
        title="Delete Customer Account"
        message="Are you sure you want to delete this customer? This action is permanent and cannot be undone."
        confirmText="Yes, Delete Account"
        isDanger={true}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingUserId(null)}
      />
    </TableCard>
  );
};

export default AdminUsersTable;
